// Задание (К-07): «Куда побежишь?», «Кому пас?», «Что делать?». Попытки по FR-QUIZ-06:
// 1 — последствие и «Давай посмотрим ещё раз!», 2 — подсказка-зона, 3 — показ правильного ответа.
// В детском режиме — никаких красных крестов и звуков неудачи (PED-4).
import { useEffect, useMemo, useRef, useState } from "react";
import { Board, piecesFromScene, staticPose, type BoardHandle, type Point } from "@brovka/board";
import type { CompiledScenario, Grade, Quiz } from "@brovka/content-types";
import { choiceIcons, SpeakerIcon } from "@brovka/ui";
import { audio } from "../lib/audio.ts";
import { phraseUrl, uiText, uiUrl } from "../lib/content/catalog.ts";
import type { LoadedContent } from "../lib/content/store.ts";
import type { Stage } from "../lib/db.ts";
import { afterAnswer } from "../kid/session.ts";
import { t } from "../i18n/ru.ts";
import { cellsOf, frameOf, gradePass, gradeWhere, mirrorStep, passConsequence, recordAnswer, runStep, whereConsequence, type Variant } from "./engine.ts";

export type QuizResult = { grade: Grade; firstGrade: Grade; attempts: number };

type Props = {
  compiled: CompiledScenario;
  quizId: string;
  variant: Variant;
  stage: Stage;
  kid: boolean;
  content: LoadedContent;
  childId?: string;
  onDone: (r: QuizResult) => void;
};

type Phase = "ask" | "busy" | "feedback" | "done";

export function QuizView({ compiled, quizId, variant, stage, kid, content, childId, onDone }: Props) {
  const s = compiled.scenario;
  const quiz = s.quizzes?.find((q) => q.id === quizId) as Quiz | undefined;
  const frame = frameOf(compiled, quizId, variant);
  const board = useRef<BoardHandle>(null);
  const [attempt, setAttempt] = useState(1);
  const [phase, setPhase] = useState<Phase>("ask");
  const [feedback, setFeedback] = useState<{ grade: Grade; text: string; parent?: string } | null>(null);
  const [hint, setHint] = useState(false);
  const [first, setFirst] = useState<Grade | null>(null);
  const [final, setFinal] = useState<Grade | null>(null);
  const [epoch, setEpoch] = useState(0);
  const askedAt = useRef(performance.now());
  const pieces = useMemo(() => piecesFromScene(s.scene), [s.scene]);
  const pose = useMemo(() => staticPose(frame?.positions ?? {}, frame?.owner), [frame, epoch]); // eslint-disable-line react-hooks/exhaustive-deps

  const say = (text: string) => audio.play(phraseUrl(compiled, text), text);

  useEffect(() => {
    if (!quiz) return;
    askedAt.current = performance.now();
    const id = setTimeout(() => void say(quiz.question), 300);
    return () => {
      clearTimeout(id);
      audio.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId, variant]);

  if (!quiz || !frame) return <p className="muted">{t.quiz.missing}</p>;

  const finish = (grade: Grade) => {
    setFinal(grade);
    setPhase("done");
  };

  const handle = async (grade: Grade, animate: () => Promise<void>, kidText: string, parentText?: string) => {
    setPhase("busy");
    const firstGrade = first ?? grade;
    if (!first) setFirst(grade);
    await recordAnswer(childId, compiled, quizId, variant, attempt, grade, performance.now() - askedAt.current);
    await animate();
    if (grade !== "bad") audio.chime(grade === "great" ? "reward" : "tap");
    setFeedback({ grade, text: kidText, parent: parentText });
    setPhase("feedback");
    // Подпись-дубль видна не меньше 1,8 с, даже если озвучка короче (PED-6: озвучка и подпись вместе).
    await Promise.all([say(kidText), new Promise((r) => setTimeout(r, 1800))]);
    const out = afterAnswer(attempt, grade);
    if (out.action === "praise") return finish(grade);
    if (out.action === "showAnswer") {
      await showAnswer();
      return finish(firstGrade === "bad" ? "bad" : firstGrade);
    }
    // Ещё раз: вернуть расстановку; на второй попытке — подсказка (подсветка вычисленной зоны).
    await audio.play(uiUrl(content, out.action === "hint" ? "hint" : "try_again"), uiText(content, out.action === "hint" ? "hint" : "try_again", t.quiz.tryAgain));
    setAttempt(out.nextAttempt ?? attempt + 1);
    if (out.action === "hint") setHint(true);
    setEpoch((e) => e + 1);
    setFeedback(null);
    setPhase("ask");
    askedAt.current = performance.now();
  };

  const showAnswer = async () => {
    setEpoch((e) => e + 1);
    await new Promise((r) => setTimeout(r, 50));
    board.current?.setPose(staticPose(frame.positions, frame.owner));
    if (quiz.type === "whereToRun") {
      const best = frame.bestPoints[stage] ?? Object.values(frame.bestPoints)[0];
      if (best) await runStep(board.current, whereConsequence(compiled, frame, quiz.hero, best as Point, "great"));
    } else if (quiz.type === "passTo") {
      const best = (frame.passTargets[stage] ?? Object.values(frame.passTargets)[0] ?? []).find((x) => x.grade === "great") ?? undefined;
      if (best) await runStep(board.current, passConsequence(compiled, frame, best.target, "great"));
    }
    const great = quiz.type === "whatToDo" ? quiz.options.find((o) => o.grade === "great") : undefined;
    setFeedback({ grade: "great", text: great?.kidFeedback ?? uiText(content, "show_answer", t.quiz.showAnswer) });
    await audio.play(uiUrl(content, "show_answer"), uiText(content, "show_answer", t.quiz.showAnswer));
  };

  const onPlace = (p: Point, touch: boolean) => {
    if (phase !== "ask" || quiz.type !== "whereToRun") return;
    void (async () => {
      const g = await gradeWhere(compiled, quiz, variant, stage, p, touch);
      const fb = quiz.feedback[g];
      await handle(g, () => runStep(board.current, whereConsequence(compiled, frame, quiz.hero, p, g)), fb.kid, fb.parent);
    })();
  };

  const onPickPlayer = (id: string) => {
    if (phase !== "ask" || quiz.type !== "passTo") return;
    const g = gradePass(frame, stage, id);
    const fb = quiz.feedback[g];
    void handle(g, () => runStep(board.current, passConsequence(compiled, frame, id, g)), fb.kid, fb.parent);
  };

  const onOption = (optId: string) => {
    if (phase !== "ask") return;
    const opts = quiz.type === "whatToDo" ? quiz.options : quiz.type === "passTo" ? quiz.extraOptions : [];
    const o = opts.find((x) => x.id === optId);
    if (!o) return;
    const branch = o.branch ? compiled.timelines[o.branch] : undefined;
    const anim = async () => {
      for (const st of branch?.steps ?? []) await runStep(board.current, variant === "mirror" ? mirrorStep(st) : st);
    };
    void handle(o.grade, anim, o.kidFeedback, o.rationale);
  };

  const teammates = quiz.type === "passTo" ? pieces.filter((p) => p.kind === "us" && p.id !== quiz.hero).map((p) => p.id) : [];
  const zone = hint || (!kid && phase === "done") ? cellsOf(frame, stage) : null;
  const options = quiz.type === "whatToDo" ? quiz.options : quiz.type === "passTo" ? quiz.extraOptions : [];
  const tone = feedback ? (feedback.grade === "bad" ? (kid ? "soft" : "warn") : "good") : "";

  return (
    <div className={`quiz ${kid ? "quiz--kid" : ""}`}>
      <div className="quiz__question">
        <button className="icon-btn" onClick={() => void say(quiz.question)} aria-label={t.common.listen}>
          <SpeakerIcon />
        </button>
        <span>{quiz.question}</span>
        {!kid ? <span className="quiz__attempt">{t.quiz.attempt(attempt)}</span> : null}
      </div>
      <div className="quiz__board">
        <Board
          ref={board}
          key={epoch}
          field={compiled.field}
          pieces={pieces}
          pose={pose}
          zone={zone ? { ...zone, showBad: !kid && phase === "done" } : null}
          placeHero={quiz.type === "whereToRun" && phase === "ask" ? quiz.hero : null}
          onPlace={onPlace}
          pickable={phase === "ask" ? teammates : []}
          onPickPlayer={onPickPlayer}
          kid={kid}
          highlight={quiz.type === "passTo" ? [quiz.hero] : []}
          ariaLabel={`${s.title}: ${quiz.question}`}
        />
      </div>
      {options.length ? (
        <div className="quiz__options" role="group" aria-label={t.quiz.options}>
          {options.map((o) => {
            const Icon = choiceIcons[o.icon];
            return (
              <button key={o.id} className="choice" onClick={() => onOption(o.id)} disabled={phase !== "ask"}>
                {Icon ? <Icon size={kid ? 72 : 48} /> : null}
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {quiz.type === "whereToRun" && phase === "ask" && !kid ? <p className="muted quiz__help">{t.quiz.whereHelp}</p> : null}
      <div className={`quiz__feedback ${tone}`} aria-live="polite">
        {feedback ? (
          <>
            <strong>{feedback.text}</strong>
            {!kid && feedback.parent ? <p>{feedback.parent}</p> : null}
          </>
        ) : null}
      </div>
      {phase === "done" && final ? (
        <button className="btn btn--primary btn--big" onClick={() => onDone({ grade: final, firstGrade: first ?? final, attempts: attempt })}>
          {t.common.next}
        </button>
      ) : null}
    </div>
  );
}
